import {
  type CreateFormFromTemplateInput,
  type CreateFormTemplateInput,
  type FormTemplateContract,
  type FormTemplateDetailContract,
  type FormTemplateSnapshotContract,
  MIAOLIN_CUSTOMER_ONBOARDING_TEMPLATE_ID,
  type PatchFormTemplateInput,
} from '@/contracts/form-templates'
import { createSyntheticMeta } from '@/contracts/questionnaire'
import type { QuestionGroupUiContract } from '@/contracts/questionnaire'
import { primaryFormId } from '@/lib/mock-data'
import { buildPublicFormUrl } from '@/lib/public-form-url'
import type { Form, LogicJump } from '@/types/form'
import { duplicateMockForm, getMockFormDetail, saveMockFormDetailSnapshot } from './forms'
import { loadFixture, savePatch } from './persistent-store'

type StoredFormTemplate = Omit<
  FormTemplateContract,
  'questionCount' | 'groupCount' | 'sourceMeta'
> & {
  snapshot?: FormTemplateSnapshotContract
}

function createSystemTemplate(): StoredFormTemplate {
  return {
    id: MIAOLIN_CUSTOMER_ONBOARDING_TEMPLATE_ID,
    workspaceId: '11111111-1111-4111-8111-111111111111',
    title: '苗林實業客戶資料建檔',
    description: '固定問卷範本，可用來建立新的苗林客戶資料建檔問卷。',
    category: 'customer_onboarding',
    status: 'active',
    isSystem: true,
    sourceFormId: primaryFormId,
    updatedAt: '2026-06-08T00:00:00.000Z',
  }
}

function getStoredTemplates(): StoredFormTemplate[] {
  const state = loadFixture() as unknown as { formTemplates?: StoredFormTemplate[] }
  const stored = state.formTemplates ?? []
  const hasSystemTemplate = stored.some((template) => template.id === MIAOLIN_CUSTOMER_ONBOARDING_TEMPLATE_ID)
  return hasSystemTemplate ? stored : [createSystemTemplate(), ...stored]
}

function saveStoredTemplates(templates: StoredFormTemplate[]) {
  savePatch({ formTemplates: templates } as never)
}

function templateCounts(template: StoredFormTemplate) {
  return {
    questionCount: template.snapshot?.questions.length ?? 0,
    groupCount: template.snapshot?.groups.length ?? 0,
  }
}

function toContract(template: StoredFormTemplate): FormTemplateContract {
  const counts = templateCounts(template)
  return {
    ...template,
    questionCount: counts.questionCount,
    groupCount: counts.groupCount,
    sourceMeta: createSyntheticMeta(),
  }
}

async function createSnapshotFromForm(formId: string): Promise<FormTemplateSnapshotContract> {
  const detail = await getMockFormDetail(formId)
  return {
    form: {
      id: detail.id,
      workspaceId: detail.workspaceId,
      title: detail.title,
      description: detail.description,
      status: detail.status,
      themeConfig: detail.themeConfig,
      settings: detail.settings,
      welcomeScreen: detail.welcomeScreen,
      thankYouScreen: detail.thankYouScreen,
      createdBy: detail.createdBy,
      updatedAt: detail.updatedAt,
    },
    questions: detail.questions,
    logicJumps: detail.logicJumps,
    groups: detail.groups,
    outline: detail.outline,
    hiddenFields: detail.hiddenFields,
  }
}

async function resolveTemplateSnapshot(
  template: StoredFormTemplate,
): Promise<FormTemplateSnapshotContract> {
  const snapshot =
    template.snapshot ??
    (template.sourceFormId ? await createSnapshotFromForm(template.sourceFormId) : null)
  if (snapshot) {
    return {
      ...snapshot,
      form: {
        ...snapshot.form,
        id: template.id,
        title: template.title,
        description: template.description,
        status: 'draft',
      },
      questions: snapshot.questions.map((question) => ({ ...question, formId: template.id })),
      groups: snapshot.groups.map((group) => ({ ...group, formId: template.id })),
    }
  }

  const now = new Date().toISOString()
  return {
    form: {
      id: template.id,
      workspaceId: template.workspaceId,
      title: template.title,
      description: template.description,
      status: 'draft',
      themeConfig: {
        backgroundColor: '#ffffff',
        textColor: '#0f172a',
        answerColor: '#ffffff',
        buttonColor: '#4f46e5',
        buttonTextColor: '#ffffff',
        accentColor: '#4f46e5',
        radius: 8,
        maxWidth: 720,
        fontFamily: 'Inter',
        progressStyle: 'thin',
      },
      settings: {},
      welcomeScreen: { enabled: false, title: template.title, description: '' },
      thankYouScreen: { title: '感謝填寫', description: '' },
      createdBy: template.createdBy ?? '',
      updatedAt: now,
    },
    questions: [],
    logicJumps: [],
    groups: [],
    outline: [],
    hiddenFields: {},
  }
}

export async function listMockFormTemplates() {
  const templates = getStoredTemplates()
    .filter((template) => template.status !== 'archived')
    .map(toContract)
    .sort((left, right) => Number(right.isSystem) - Number(left.isSystem) || right.updatedAt.localeCompare(left.updatedAt))
  return {
    data: templates,
    meta: {
      ...createSyntheticMeta(),
      total: templates.length,
    },
  }
}

export async function getMockFormTemplateDetail(
  templateId: string,
): Promise<FormTemplateDetailContract> {
  const template =
    getStoredTemplates().find((item) => item.id === templateId) ?? createSystemTemplate()
  const snapshot = await resolveTemplateSnapshot(template)
  return {
    ...toContract({
      ...template,
      snapshot,
    }),
    snapshot,
  }
}

export async function createMockFormTemplateFromForm(
  formId: string,
  input: Omit<CreateFormTemplateInput, 'sourceFormId' | 'snapshot'>,
) {
  const snapshot = await createSnapshotFromForm(formId)
  const now = new Date().toISOString()
  const template: StoredFormTemplate = {
    id: crypto.randomUUID(),
    workspaceId: snapshot.form.workspaceId,
    title: input.title,
    description: input.description ?? '',
    category: input.category ?? 'general',
    status: 'active',
    isSystem: false,
    sourceFormId: formId,
    snapshot,
    createdBy: snapshot.form.createdBy,
    updatedAt: now,
  }
  saveStoredTemplates([template, ...getStoredTemplates()])
  return getMockFormTemplateDetail(template.id)
}

export async function patchMockFormTemplate(templateId: string, patch: PatchFormTemplateInput) {
  const templates = getStoredTemplates()
  const current = templates.find((template) => template.id === templateId) ?? createSystemTemplate()
  const next: StoredFormTemplate = {
    ...current,
    title: patch.title ?? current.title,
    description: patch.description ?? current.description,
    category: patch.category ?? current.category,
    status: patch.status ?? current.status,
    snapshot: patch.snapshot ?? current.snapshot,
    updatedAt: new Date().toISOString(),
  }
  saveStoredTemplates([next, ...templates.filter((template) => template.id !== templateId)])
  return getMockFormTemplateDetail(templateId)
}

function cloneLogicJumpForForm(
  jump: LogicJump,
  questionIdMap: Map<string, string>,
  groupIdMap: Map<string, string>,
  order: number,
): LogicJump {
  return {
    ...structuredClone(jump),
    id: crypto.randomUUID(),
    questionId: questionIdMap.get(jump.questionId) ?? jump.questionId,
    targetQuestionId:
      jump.targetQuestionId === null
        ? null
        : (questionIdMap.get(jump.targetQuestionId) ?? jump.targetQuestionId),
    targetGroupId: jump.targetGroupId ? (groupIdMap.get(jump.targetGroupId) ?? jump.targetGroupId) : null,
    order,
    conditions: jump.conditions.map((condition) => ({
      ...condition,
      field: questionIdMap.get(condition.field) ?? condition.field,
    })),
    displayTargets: jump.displayTargets?.map((target) => {
      if (target.type === 'group') {
        const groupId = groupIdMap.get(target.groupId) ?? target.groupId
        return { ...target, id: `group:${groupId}`, groupId }
      }
      if (target.type === 'group_question') {
        const groupId = groupIdMap.get(target.groupId) ?? target.groupId
        const questionId = questionIdMap.get(target.questionId) ?? target.questionId
        return { ...target, id: `group_question:${groupId}:${questionId}`, groupId, questionId }
      }
      const questionId = questionIdMap.get(target.questionId) ?? target.questionId
      return {
        ...target,
        id: `question:${questionId}`,
        questionId,
        groupId: target.groupId ? (groupIdMap.get(target.groupId) ?? target.groupId) : null,
      }
    }),
  }
}

export async function createMockFormFromTemplate(
  templateId: string,
  input: CreateFormFromTemplateInput = {},
) {
  const template = await getMockFormTemplateDetail(templateId)
  const duplicated = await duplicateMockForm(template.snapshot.form.id)
  const formId = duplicated.id
  const questionIdMap = new Map(
    template.snapshot.questions.map((question) => [question.id, crypto.randomUUID()]),
  )
  const groupIdMap = new Map(
    template.snapshot.groups.map((group) => [group.id, crypto.randomUUID()]),
  )
  const form: Form = {
    ...structuredClone(template.snapshot.form),
    id: formId,
    title: input.title?.trim() || `${template.title}（新問卷）`,
    description: input.description ?? template.description,
    status: 'draft',
    welcomeScreen: {
      ...template.snapshot.form.welcomeScreen,
      title: input.title?.trim() || template.snapshot.form.welcomeScreen.title || template.title,
    },
    updatedAt: new Date().toISOString(),
  }
  const questions = template.snapshot.questions.map((question, orderIndex) => {
    const nextQuestionId = questionIdMap.get(question.id) ?? crypto.randomUUID()
    return {
      ...structuredClone(question),
      id: nextQuestionId,
      formId,
      groupId: question.groupId ? groupIdMap.get(question.groupId) : undefined,
      orderIndex,
      choices: question.choices.map((choice, choiceIndex) => ({
        ...choice,
        id: crypto.randomUUID(),
        questionId: nextQuestionId,
        orderIndex: choiceIndex,
      })),
    }
  })
  const groups: QuestionGroupUiContract[] = template.snapshot.groups.map((group, orderIndex) => {
    const nextGroupId = groupIdMap.get(group.id) ?? crypto.randomUUID()
    return {
      ...structuredClone(group),
      id: nextGroupId,
      formId,
      orderIndex,
      questionIds: group.questionIds.map((questionId) => questionIdMap.get(questionId) ?? questionId),
    }
  })
  const logicJumps = template.snapshot.logicJumps.map((jump, order) =>
    cloneLogicJumpForForm(jump, questionIdMap, groupIdMap, order),
  )
  const outline = template.snapshot.outline.map((item) =>
    item.kind === 'question'
      ? {
          kind: 'question' as const,
          questionId: questionIdMap.get(item.questionId) ?? item.questionId,
          groupInstanceId: null,
        }
      : {
          kind: 'group' as const,
          groupInstanceId: groupIdMap.get(item.groupInstanceId) ?? item.groupInstanceId,
        },
  )

  saveMockFormDetailSnapshot({
    form,
    questions,
    logicJumps,
    groups,
    outline,
  })
  savePatch((current) => ({
    forms: [form, ...current.forms.filter((item) => item.id !== form.id)],
    formOrder: [form.id, ...current.formOrder.filter((id) => id !== form.id)],
    shareSettings: {
      ...current.shareSettings,
      [form.id]: {
        formId: form.id,
        publicUrl: buildPublicFormUrl(form.id),
        isEnabled: false,
        closeAt: null,
        maxResponses: null,
        allowRepeat: false,
        defaultSalesCode: '',
        defaultCampaignCode: '',
        hiddenFields: {},
        qrCodeUrl: '',
        sourceMeta: createSyntheticMeta(),
      },
    },
  }))

  return getMockFormDetail(form.id)
}
